import {
  computeSessionExpectedFromWorkerSession,
  buildSessionDeviationDetail
} from '../finance/sessionCashflow';
import { readExpensesSyncForBusiness } from '../../utils/expensesReadSource';
import { readArrayFieldFromMergedBlob } from '../../utils/businessStateSync';
import { normalizeBusinessStorageId, getCurrentBusinessId } from '../../utils/sharedCore';
import { getCashRegisterOutflowContextForBusiness } from '../../utils/cashRegisterOutflowContext';

/**
 * Écart / Soll-Ist — identique WorkerSessionManager (Anzeige Session).
 * Une seule source pour Session-Tabelle, Detail-Modal et Écart-Modal.
 */
export function computeWorkerSessionDifferences(session, registerOutflowContext = null) {
  if (!session) {
    return {
      totalDifference: 0,
      deviationDetail: null
    };
  }

  const initialAmount = Number(session.initialAmount || session.cashAmount || 0);
  const finalCashAmount = Number(
    session.finalCashAmount || (session.logoutTime ? session.cashAmount : session.initialAmount) || 0
  );
  const initialElectronic = Number(session.initialElectronicAmount ?? session.electronicAmount) || 0;
  const finalElectronic =
    session.finalElectronicAmount != null
      ? Number(session.finalElectronicAmount) || 0
      : Number(session.electronicAmount) || 0;

  const ctx =
    registerOutflowContext ||
    getCashRegisterOutflowContextForBusiness(
      session.businessId || normalizeBusinessStorageId(getCurrentBusinessId())
    );

  const businessId = ctx.businessId || normalizeBusinessStorageId(getCurrentBusinessId());

  let sessionFlow;
  try {
    sessionFlow = computeSessionExpectedFromWorkerSession(session, {
      expenses: readExpensesSyncForBusiness(businessId) || [],
      supermarketPayments: readArrayFieldFromMergedBlob(businessId, 'supermarketPayments') || [],
      invoicePayments: readArrayFieldFromMergedBlob(businessId, 'invoicePayments') || [],
      registerSessions: ctx.registerSessions,
      activeRegisterSession: ctx.activeRegisterSession,
      allSales: readArrayFieldFromMergedBlob(businessId, 'sales') || []
    });
  } catch {
    sessionFlow = computeSessionExpectedFromWorkerSession(session, {
      registerSessions: ctx.registerSessions,
      activeRegisterSession: ctx.activeRegisterSession,
      allSales: businessId ? (readArrayFieldFromMergedBlob(businessId, 'sales') || []) : null
    });
  }

  const expectedCash = sessionFlow.expectedCash;
  const expectedElectronic = sessionFlow.expectedElectronic;
  const salesDifference = finalCashAmount - expectedCash;
  const electronicDifference = finalElectronic - expectedElectronic;
  const totalDifference = salesDifference + electronicDifference;
  const deviationDetail = buildSessionDeviationDetail(session, sessionFlow);

  let predecessorDiscrepancy = null;
  if (session.predecessorInfo) {
    if (session.predecessorInfo.finalAmount > 0) {
      predecessorDiscrepancy = initialAmount - session.predecessorInfo.finalAmount;
    } else {
      predecessorDiscrepancy = 0;
    }
  }

  return {
    cashRegisterDifference: finalCashAmount - initialAmount,
    initialAmount,
    finalCashAmount,
    initialElectronic,
    finalElectronic,
    cashSales: sessionFlow.cashSales,
    electronicSales: sessionFlow.electronicSales,
    cashOutgoing: sessionFlow.cashOutgoing,
    electronicOutgoing: sessionFlow.electronicOutgoing,
    cashPaymentsOutgoing:
      sessionFlow?.cashPaymentsOutgoing ?? sessionFlow?.outgoingBreakdown?.payments?.cash ?? 0,
    cashExpensesOutgoing:
      sessionFlow?.cashExpensesOutgoing ?? sessionFlow?.outgoingBreakdown?.expenses?.cash ?? 0,
    expectedCash,
    expectedElectronic,
    salesDifference,
    electronicDifference,
    totalDifference,
    initialTotal: initialAmount + initialElectronic,
    expectedTotal: expectedCash + expectedElectronic,
    finalTotal: finalCashAmount + finalElectronic,
    outgoingTotal: (sessionFlow.cashOutgoing || 0) + (sessionFlow.electronicOutgoing || 0),
    deviationDetail,
    predecessorDiscrepancy
  };
}

/** Trouve la session affichée (enrichie) liée à un écart enregistré. */
export function findDisplaySessionForDeviation(deviationRecord, displaySessions = []) {
  if (!deviationRecord || !Array.isArray(displaySessions)) return null;

  const sessionId = deviationRecord.sessionId;
  const workerId = deviationRecord.workerId ?? deviationRecord.previousWorkerId;
  if (!sessionId || !workerId) return null;

  return (
    displaySessions.find(
      (candidate) =>
        String(candidate.workerId) === String(workerId) &&
        (String(candidate.loginTime) === String(sessionId) ||
          String(candidate.id) === String(sessionId))
    ) || null
  );
}

export function resolveEcartDetailFromDisplaySessions(
  deviationRecord,
  displaySessions,
  registerOutflowContext = null
) {
  const session = findDisplaySessionForDeviation(deviationRecord, displaySessions);
  if (!session?.logoutTime) return null;
  return computeWorkerSessionDifferences(session, registerOutflowContext).deviationDetail;
}

export function resolveEcartAmountFromDisplaySessions(
  deviationRecord,
  displaySessions,
  registerOutflowContext = null
) {
  const session = findDisplaySessionForDeviation(deviationRecord, displaySessions);
  if (!session?.logoutTime) return null;
  return computeWorkerSessionDifferences(session, registerOutflowContext).totalDifference;
}
